import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { STRIPE_URL } from "@/lib/links";
import { track } from "@/lib/analytics";

const NAV_LINKS = [
  { label: "Problemas", href: "#problemas" },
  { label: "Diagnóstico", href: "#diagnostico" },
  { label: "Método", href: "#metodo" },
  { label: "Consultoría", href: "#consultoria" },
  { label: "Sobre mí", href: "#sobre-mi" },
  { label: "Credenciales", href: "#credenciales" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (el && window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -80, duration: 1.25 });
    } else if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#06231D]/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <a href="#top" onClick={(e) => handleAnchor(e, "#top")} className="flex items-center gap-3 group" data-testid="nav-logo">
          <img
            src="/logo-optimizarrhh.webp"
            alt="OptimizaRRHH · Consultoría de talento"
            className="h-12 w-auto"
            data-testid="nav-logo-img"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => handleAnchor(e, l.href)}
              className="text-[13px] uppercase tracking-[0.16em] text-white/70 hover:text-white link-underline"
              data-testid={`nav-link-${l.href.replace('#','')}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={STRIPE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            onClick={() => track("cta_click", { location: "navbar", product: "diagnostic" })}
            className="hidden md:inline-flex items-center gap-2 px-5 h-11 rounded-full brand-gradient text-white text-sm font-medium cta-lift"
          >
            Empieza el diagnóstico
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </a>
          <button
            aria-label="menu"
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-11 h-11 grid place-items-center rounded-full border border-white/15 text-white"
          >
            <div className="flex flex-col gap-1.5">
              <span className={`block w-4 h-px bg-white transition-transform ${open ? "translate-y-[3px] rotate-45" : ""}`} />
              <span className={`block w-4 h-px bg-white transition-transform ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
            </div>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden bg-[#06231D] border-t border-white/5 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => handleAnchor(e, l.href)}
                  className="text-white/80 text-lg font-display"
                  data-testid={`mobile-nav-link-${l.href.replace('#','')}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href={STRIPE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center h-12 rounded-full brand-gradient text-white font-medium"
                data-testid="mobile-nav-cta"
                onClick={() => track("cta_click", { location: "mobile_nav", product: "diagnostic" })}
              >
                Empieza el diagnóstico
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
